// utils/appointmentUtils.js
import { TIME_SLOTS_CONFIG } from '../constants/calendar';
import {
  getDurationMinutes,
  formatDuration,
  isSameDay,
  parseDate,
  parseTimeSlot as parseTimeSlotUtil,
  startOfDay,
  endOfDay,
  toAPIDate,
  toLocalDateTime,
  isValidDate
} from './dateUtils';

/**
 * Calculate the visual position and size of an appointment in the calendar grid
 * @param {Object} appointment - The appointment object with EventStart and EventEnd
 * @param {Array} timeSlots - Array of time slot strings
 * @returns {Object} Position and size information
 */
export const calculateAppointmentPosition = (appointment, timeSlots) => {
  const startTime = new Date(appointment.EventStart);
  const endTime = new Date(appointment.EventEnd);

  const durationMinutes = getDurationMinutes(startTime, endTime);
  const startHour = startTime.getHours();
  const startMinute = startTime.getMinutes();

  // Calculate minutes from start hour (default 8 AM)
  const startMinutesFromBase = (startHour - TIME_SLOTS_CONFIG.START_HOUR) * 60 + startMinute;
  const startSlotIndex = Math.floor(startMinutesFromBase / TIME_SLOTS_CONFIG.SLOT_INTERVAL_MINUTES);

  // Calculate height based on duration
  const heightInPixels = durationMinutes * TIME_SLOTS_CONFIG.PIXELS_PER_MINUTE;
  const slotsSpanned = Math.ceil(durationMinutes / TIME_SLOTS_CONFIG.SLOT_INTERVAL_MINUTES);

  return {
    startSlotIndex,
    slotsSpanned,
    durationMinutes,
    heightInPixels
  };
};

/**
 * Get CSS class name for appointment type
 * @param {string} title - Appointment title/type
 * @returns {string} CSS class name
 */
export const getAppointmentTypeClass = (title) => {
  if (!title) return 'default';
  
  // Convert to lowercase and replace spaces with hyphens
  return title.toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, ''); // Remove special characters
};

/**
 * Filter appointments for a specific day
 * @param {Array} appointments - Array of appointment objects
 * @param {Date} date - Target date
 * @returns {Array} Filtered appointments for the day
 */
export const getAppointmentsForDay = (appointments, date) => {
  return appointments.filter(apt => {
    const aptStart = new Date(apt.EventStart);
    return isSameDay(aptStart, date);
  });
};

/**
 * Filter appointments for a specific stylist
 * @param {Array} appointments - Array of appointment objects
 * @param {Object} stylist - Stylist object with id and name
 * @returns {Array} Filtered appointments for the stylist
 */
export const getStylistAppointments = (appointments, stylist) => {
  if (!stylist) return [];
  
  return appointments.filter(apt => {
    const matchesById = apt.MWuserID === stylist.id;
    const matchesByName = apt.MeetingWith === stylist.name;
    return matchesById || matchesByName;
  });
};

/**
 * Filter appointments for all staff in an office
 * @param {Array} appointments - Array of appointment objects
 * @param {Array} officeStaff - Array of staff objects with id and name
 * @returns {Array} Filtered appointments for the office
 */
export const getOfficeAppointments = (appointments, officeStaff) => {
  const officeStaffIds = officeStaff.map(staff => staff.id);
  const officeStaffNames = officeStaff.map(staff => staff.name);
  
  return appointments.filter(apt => {
    const matchesById = apt.MWuserID && officeStaffIds.includes(apt.MWuserID);
    const matchesByName = apt.MeetingWith && officeStaffNames.includes(apt.MeetingWith);
    return matchesById || matchesByName;
  });
};

/**
 * Sort appointments by start time
 * @param {Array} appointments - Array of appointment objects
 * @returns {Array} Sorted appointments
 */
export const sortAppointmentsByTime = (appointments) => {
  return [...appointments].sort((a, b) => {
    const timeA = new Date(a.EventStart).getTime();
    const timeB = new Date(b.EventStart).getTime();
    return timeA - timeB;
  });
};

/**
 * Check if an appointment overlaps with a time range
 * @param {Object} appointment - Appointment object
 * @param {Date} startTime - Range start time
 * @param {Date} endTime - Range end time
 * @returns {boolean} True if appointment overlaps with the range
 */
export const appointmentOverlapsRange = (appointment, startTime, endTime) => {
  const aptStart = new Date(appointment.EventStart);
  const aptEnd = new Date(appointment.EventEnd);
  
  return aptStart < endTime && aptEnd > startTime;
};

/**
 * Get appointments that start in a specific time slot
 * @param {Array} appointments - Array of appointment objects
 * @param {number} slotIndex - Time slot index
 * @param {Array} timeSlots - Array of time slot strings
 * @returns {Array} Appointments starting in the slot
 */
export const getAppointmentsForSlot = (appointments, slotIndex, timeSlots) => {
  return appointments.filter(apt => {
    const position = calculateAppointmentPosition(apt, timeSlots);
    return position.startSlotIndex === slotIndex;
  });
};

/**
 * Check if appointment is long duration (over 3 hours)
 * @param {Object} appointment - Appointment object
 * @returns {boolean} True if appointment is long duration
 */
export const isLongAppointment = (appointment) => {
  const durationMinutes = getDurationMinutes(appointment.EventStart, appointment.EventEnd);
  return durationMinutes > 180; // 3 hours
};

/**
 * Format appointment duration as human readable string
 * @param {Object} appointment - Appointment object
 * @returns {string} Formatted duration (e.g., "1h 30m")
 */
export const formatAppointmentDuration = (appointment) => {
  const durationMinutes = getDurationMinutes(appointment.EventStart, appointment.EventEnd);
  return formatDuration(durationMinutes);
};

/**
 * Get holiday information for a specific date
 * @param {Array} holidays - Array of holiday objects
 * @param {Date} date - Target date
 * @returns {Array} Holidays for the date
 */
export const getHolidaysForDay = (holidays, date) => {
  return holidays.filter(holiday => {
    // Parse as local date to avoid timezone conversion
    const holidayDateStr = holiday.HolidayDate.split('T')[0]; // Get just "2025-12-18"
    const holidayDate = new Date(holidayDateStr + 'T00:00:00'); // Parse as local time
    
    return isSameDay(holidayDate, date);
  });
};

/**
 * Check if a date has any holidays
 * @param {Array} holidays - Array of holiday objects
 * @param {Date} date - Target date
 * @returns {boolean} True if date has holidays
 */
export const hasHolidays = (holidays, date) => {
  return getHolidaysForDay(holidays, date).length > 0;
};

/**
 * Generate time slots based on configuration
 * @returns {Array} Array of time slot strings in 12-hour format
 */
export const generateTimeSlots = () => {
  const slots = [];
  const totalMinutes = (TIME_SLOTS_CONFIG.END_HOUR - TIME_SLOTS_CONFIG.START_HOUR) * 60;
  const slotCount = totalMinutes / TIME_SLOTS_CONFIG.SLOT_INTERVAL_MINUTES;
  
  for (let i = 0; i < slotCount; i++) {
    const totalMinutesFromStart = TIME_SLOTS_CONFIG.START_HOUR * 60 + (i * TIME_SLOTS_CONFIG.SLOT_INTERVAL_MINUTES);
    const hour = Math.floor(totalMinutesFromStart / 60);
    const minute = totalMinutesFromStart % 60;
    
    // Convert to 12-hour format
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const ampm = hour < 12 ? 'AM' : 'PM';
    
    slots.push(`${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`);
  }
  
  return slots;
};

/**
 * Find which office and stylist an appointment belongs to
 * @param {Object} appointment - Appointment object
 * @param {Object} offices - Offices configuration object
 * @returns {Object|null} Object with office key and stylist info, or null if not found
 */
export const findAppointmentOfficeAndStylist = (appointment, offices) => {
  for (const [officeKey, office] of Object.entries(offices)) {
    const stylist = office.staff.find(staff => 
      staff.id === appointment.MWuserID || staff.name === appointment.MeetingWith
    );
    
    if (stylist) {
      return {
        officeKey,
        office: office.name,
        stylist
      };
    }
  }
  
  return null;
};

/**
 * Get display color class for appointment based on stylist or type
 * @param {Object} appointment - Appointment object
 * @param {Object} stylist - Stylist object (optional)
 * @returns {string} CSS class for coloring
 */
export const getAppointmentColorClass = (appointment, stylist = null) => {
  // You can implement custom logic here for different color schemes
  // For now, using appointment type
  const typeClass = getAppointmentTypeClass(appointment.Title);
  
  // Could also add stylist-based coloring:
  // const stylistClass = stylist ? `stylist-${stylist.id}` : '';
  
  return typeClass;
};

/**
 * Validate if appointment times are within business hours
 * @param {Object} appointment - Appointment object
 * @returns {boolean} True if within business hours
 */
export const isWithinBusinessHours = (appointment) => {
  const startTime = new Date(appointment.EventStart);
  const endTime = new Date(appointment.EventEnd);
  
  const startHour = startTime.getHours();
  const endHour = endTime.getHours();
  const endMinute = endTime.getMinutes();
  
  // Check if appointment is within configured business hours
  const isStartValid = startHour >= TIME_SLOTS_CONFIG.START_HOUR;
  const isEndValid = endHour < TIME_SLOTS_CONFIG.END_HOUR || 
                     (endHour === TIME_SLOTS_CONFIG.END_HOUR && endMinute === 0);
  
  return isStartValid && isEndValid;
};


// Add these new functions to your existing appointmentUtils.js file:

/**
 * Helper function to parse event dates
 * @param {string} dateString - Date string to parse
 * @returns {Date|null} Parsed date or null if invalid
 * @deprecated Use parseDate from dateUtils instead
 */
export const parseEventDate = (dateString) => {
  return parseDate(dateString);
};



/**
 * Format appointment date for display in dropdowns
 * @param {string} dateString - Event start date string
 * @returns {string} Formatted date string
 */
export const formatAppointmentForDropdown = (dateString) => {
  const eventDate = parseEventDate(dateString);
  return eventDate ? eventDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }) : 'Invalid Date';
};


export class AppointmentServiceUtils {
  /**
   * Format date for API calls
   * @param {Date} date - Date object
   * @returns {string} Formatted date string (YYYY-MM-DD)
   * @deprecated Use toAPIDate from dateUtils instead
   */
  static formatDateForAPI(date) {
    return toAPIDate(date);
  }

  /**
   * Format local datetime for API calls
   * @param {Date} date - Date object
   * @returns {string} Formatted datetime string (YYYY-MM-DDTHH:mm)
   * @deprecated Use toLocalDateTime from dateUtils instead
   */
  static formatLocalDateTime(date) {
    return toLocalDateTime(date);
  }

  /**
   * Parse 12-hour time slot to 24-hour format
   * @param {string} timeString - Time string like "2:00 PM"
   * @returns {object} Object with hours and minutes in 24-hour format
   * @deprecated Use parseTimeSlot from dateUtils instead
   */
  static parseTimeSlot(timeString) {
    return parseTimeSlotUtil(timeString);
  }

  /**
   * Check if appointment times are valid
   * @param {string} startTime - Start time string
   * @param {string} endTime - End time string
   * @returns {boolean} True if valid
   */
  static validateAppointmentTimes(startTime, endTime) {
    const start = new Date(startTime);
    const end = new Date(endTime);

    // Check if dates are valid
    if (!isValidDate(start) || !isValidDate(end)) {
      return false;
    }

    // Check if end is after start
    if (end <= start) {
      return false;
    }

    // Check minimum duration (15 minutes)
    const durationMinutes = getDurationMinutes(start, end);
    return durationMinutes >= 15;
  }

  /**
   * Calculate appointment duration in minutes
   * @param {string} startTime - Start time string
   * @param {string} endTime - End time string
   * @returns {number} Duration in minutes
   * @deprecated Use getDurationMinutes from dateUtils instead
   */
  static calculateDuration(startTime, endTime) {
    return getDurationMinutes(startTime, endTime);
  }

/**
 * Check if an appointment spans a given date
 */
static doesAppointmentSpanDate(appointment, date) {
  const aptStart = new Date(appointment.EventStart);
  const aptEnd = new Date(appointment.EventEnd);

  const dayStart = startOfDay(date);
  const dayEnd = endOfDay(date);

  // Appointment spans the date if it starts before/on day end and ends after/on day start
  return aptStart <= dayEnd && aptEnd >= dayStart;
}

/**
 * Get effective times for multi-day appointments
 */
static getEffectiveAppointmentTimes(appointment, viewDate) {
  const aptStart = new Date(appointment.EventStart);
  const aptEnd = new Date(appointment.EventEnd);

  const dayStart = startOfDay(viewDate);
  const dayEnd = endOfDay(viewDate);

  // Use the later of appointment start or day start
  const effectiveStart = aptStart > dayStart ? aptStart : dayStart;

  // Use the earlier of appointment end or day end
  const effectiveEnd = aptEnd < dayEnd ? aptEnd : dayEnd;

  return { effectiveStart, effectiveEnd };
}



  }